import { DatePickerProps } from 'components/common/DatePicker/DatePicker';
import { DateRangePickerProps } from 'components/common/DateRangePicker/DateRangePicker';
import FutureDateRangePicker from 'components/common/DateRangePicker/FutureDateRangePicker';
import { useField, useFormikContext } from 'formik';
import { defaultTo, get, set } from 'lodash';
import React, { useCallback, useRef } from 'react';
import { FieldLayoutProps, withFieldLayout } from 'hoc/withFieldLayout';
import { FormikFieldProps } from 'hoc/withFormikField';

type FormikDayRangeExtraProps = {
  startDateFormikName: string;
  endDateFormikName: string;
  format?: string;
  startDatePickerDisabled?: boolean;
  endDatePickerDisabled?: boolean;
  validateOnPopOverClose?: boolean;
  validate?: (startDate: string, endDate: string) => void;
  onChange?: (startDate: string, endDate: string) => void;
};

export interface FormikDateRangePickerProps
  extends
    FormikDayRangeExtraProps,
    Omit<DatePickerProps, 'disabled' | 'onChange' | 'date' | 'hasError'>,
    Omit<FormikFieldProps, 'onChange' | 'validate'> {}
export interface FormikDateRangePickerWithFieldLayoutProps
  extends FormikDateRangePickerProps, Omit<FieldLayoutProps, 'name'> {}
export type FormikDateRangePickerFieldProps =
  | ({ withFieldLayout?: true } & FormikDateRangePickerWithFieldLayoutProps)
  | ({ withFieldLayout: false } & FormikDateRangePickerProps);

export const DateRangePickerField = withFieldLayout<
  DateRangePickerProps & FieldLayoutProps
>(FutureDateRangePicker);
DateRangePickerField.defaultProps = {
  fieldContentWidth: 'auto'
};

const useFormikDateRangePickerHook = (
  name: string,
  startDateFormikName: string,
  endDateFormikName: string,
  validateOnPopOverClose?: boolean,
  validate?: (startDate: string, endDate: string) => void,
  onChange?: (startDate: string, endDate: string) => void,
  onPopOverClosed?: () => Promise<void>
) => {
  const { values, submitCount, setValues, setFieldTouched, validateForm } =
    useFormikContext<any>();
  const startDate = get(values, startDateFormikName);
  const endDate = get(values, endDateFormikName);

  // Use a ref to track the latest dates so validation always sees current values,
  // even when called synchronously during setValues before re-render.
  const pendingDatesRef = useRef({ startDate, endDate });
  pendingDatesRef.current = { startDate, endDate };

  const hanldeValidate = useCallback(() => {
    const { startDate: s, endDate: e } = pendingDatesRef.current;
    return validate && validate(s, e);
  }, [validate]);

  const [, { touched: formikTouched, error }] = useField({
    name,
    validate: validate ? hanldeValidate : undefined
  });
  const touched = defaultTo(submitCount, 0) > 0 || formikTouched;

  const handleChange = useCallback(
    (startDate, endDate) => {
      // Update ref before setValues so that synchronous validation sees the new dates
      pendingDatesRef.current = { startDate, endDate };
      setValues((prev) => {
        const newValues = {
          ...prev
        };
        set(newValues, startDateFormikName, startDate);
        set(newValues, endDateFormikName, endDate);
        return newValues;
      }, !validateOnPopOverClose);
      setFieldTouched(name, true);
      // formik issues 2266 workaround
      setTimeout(() => {
        onChange && onChange(startDate, endDate);
      });
    },
    [
      name,
      startDateFormikName,
      endDateFormikName,
      validateOnPopOverClose,
      setValues,
      setFieldTouched,
      onChange
    ]
  );

  return {
    error,
    touched,
    startDate,
    endDate,
    onChange: handleChange,
    onPopOverClosed: validateOnPopOverClose
      ? async () => {
          onPopOverClosed && (await onPopOverClosed());
          validateForm();
        }
      : onPopOverClosed
  };
};

const MemorizedDateRangePickerContent = React.memo(FutureDateRangePicker);

export const FormikDateRangePickerField: React.FC<
  FormikDateRangePickerFieldProps
> = ({
  name,
  startDateFormikName,
  endDateFormikName,
  withFieldLayout,
  validateOnPopOverClose,
  onChange,
  validate,
  onPopOverClosed,
  ...props
}: FormikDateRangePickerFieldProps) => {
  const { error, touched, ...otherProps } = useFormikDateRangePickerHook(
    name,
    startDateFormikName,
    endDateFormikName,
    validateOnPopOverClose,
    validate,
    onChange,
    onPopOverClosed ? async () => onPopOverClosed() : undefined
  );
  const hasError = error !== undefined && touched;
  if (withFieldLayout === false) {
    return (
      <MemorizedDateRangePickerContent
        {...props}
        name={name}
        {...otherProps}
        hasError={hasError}
      />
    );
  } else {
    return (
      <DateRangePickerField
        {...props}
        {...otherProps}
        name={name}
        error={hasError ? error : undefined}
        hasError={hasError}
      />
    );
  }
};
